import { ConcurrencyTarget } from "../../models/concurrencyTargetEnum";
import { get } from "../../utils/net";

import type {
  ElixhauserAnalyzeResult,
  ElixhauserCodeResult,
  ElixhauserFlag,
  ElixhauserServiceCodeResult,
  ElixhauserServiceFlag,
  ElixhauserServiceResult,
} from "../../models/elixhauser";
import type { EncounterEntity } from "../../models/encounterEntity";

const baseURL = process.env.REACT_APP_API_PATH;

const firstDefined = <T>(...values: (T | null | undefined)[]): T | undefined => {
  const found = values.find((value) => value !== undefined && value !== null);
  return found ?? undefined;
};

const mapFlag = (flag: ElixhauserServiceFlag): ElixhauserFlag => ({
  flag: firstDefined(flag.Flag, flag.flag) ?? null,
  description: firstDefined(flag.Description, flag.description) ?? null,
  mortalityWeight: firstDefined(flag.MortalityWeight, flag.mortalityWeight) ?? null,
  readmissionWeight: firstDefined(flag.ReadmissionWeight, flag.readmissionWeight) ?? null,
});

const mapCodeResult = (codeResult: ElixhauserServiceCodeResult): ElixhauserCodeResult => ({
  viewId: firstDefined(codeResult.ViewId, codeResult.viewId) ?? null,
  code: firstDefined(codeResult.Code, codeResult.code) ?? null,
  flags: firstDefined(codeResult.Flags, codeResult.flags) ?? [],
  mortalityWeightTotal:
    firstDefined(codeResult.MortalityWeightTotal, codeResult.mortalityWeightTotal) ?? null,
  readmissionWeightTotal:
    firstDefined(codeResult.ReadmissionWeightTotal, codeResult.readmissionWeightTotal) ?? null,
});

export const mapElixhauserServiceResult = (
  result: ElixhauserServiceResult,
): ElixhauserAnalyzeResult => ({
  flags: (firstDefined(result.Flags, result.flags) ?? []).map(mapFlag),
  codeResults: (firstDefined(result.CodeResults, result.codeResults) ?? []).map(mapCodeResult),
  mortalityIndexTotal: firstDefined(result.MortalityIndexTotal, result.mortalityIndexTotal) ?? null,
  readmissionIndexTotal:
    firstDefined(result.ReadmissionIndexTotal, result.readmissionIndexTotal) ?? null,
  version: firstDefined(result.Version, result.version) ?? null,
});

const analyze = (enc: EncounterEntity): Promise<ElixhauserAnalyzeResult> => {
  const version = enc.concurrencyVersion ?? "";
  const url = `${baseURL}/api/CurrentEncounter/elixhauser/${enc.id}`;

  return get(url, {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers: {
      "If-Match": version,
    },
    useExactPath: true,
  }).then((response) => mapElixhauserServiceResult(response.body as ElixhauserServiceResult));
};

export const elixhauserAPI = {
  analyze,
};
